// File: frontend/src/pages/StudentLeavePage.jsx

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { leaveRequestsAPI, studentPortalAPI } from '../services/api';

const REASON_OPTIONS = [
  { value: 'sick', label: '病假 (Sick Leave)' },
  { value: 'personal', label: '事假 (Personal Leave)' },
  { value: 'exam', label: '學校考試 (School Exam)' },
  { value: 'official', label: '公假 (Official Leave)' },
  { value: 'other', label: '其他 (Other)' },
];

export default function StudentLeavePage() {
  const [classes, setClasses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // New Request Form state
  const [selectedClassId, setSelectedClassId] = useState('');
  const [requestDate, setRequestDate] = useState('');
  const [reasonCategory, setReasonCategory] = useState('sick');
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classList, requestList] = await Promise.all([
        studentPortalAPI.getMyClasses().catch(() => []),
        leaveRequestsAPI.getAll().catch(() => [])
      ]);
      setClasses(classList);
      setRequests(requestList);
      if (classList.length > 0) {
        setSelectedClassId(classList[0].id);
      }
    } catch (err) {
      console.error('Failed to load leave data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requestDate) {
      toast.error('Please select a date for your leave.');
      return;
    }

    setSubmitting(true);
    try {
      await leaveRequestsAPI.create({
        class_id: selectedClassId || null,
        request_date: requestDate,
        reason_category: reasonCategory,
        reason_text: reasonText,
      });

      toast.success('Leave request submitted! Teacher will review it.');
      setRequestDate('');
      setReasonText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit leave request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Cancel this pending leave request?')) {
      try {
        await leaveRequestsAPI.delete(id);
        toast.success('Leave request cancelled.');
        fetchData();
      } catch (err) {
        toast.error('Failed to cancel request.');
      }
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Overview Banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-6 rounded-xl shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Leave & Make-Up (請假與補課)</h2>
          <p className="text-emerald-100 text-sm mt-1">
            Submit absence requests in advance. Approved requests earn make-up class credits.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Request Form */}
        <div className="md:col-span-1 bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-fit">
          <h3 className="font-bold text-gray-800 dark:text-gray-200 text-base mb-4 flex items-center gap-2">
            📝 Submit Leave (提出請假)
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4 text-sm">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Class (請假班級)</label>
              <select
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                className="w-full border rounded-lg p-2.5 text-xs bg-white dark:bg-gray-800 focus:ring-2 focus:ring-emerald-500"
              >
                {classes.length > 0 ? (
                  classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.day} {c.start_time})
                    </option>
                  ))
                ) : (
                  <option value="">General Class</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Leave Date (請假日期)</label>
              <input
                type="date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                className="w-full border rounded-lg p-2 text-xs focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Reason (請假類別)</label>
              <select
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value)}
                className="w-full border rounded-lg p-2.5 text-xs bg-white dark:bg-gray-800 focus:ring-2 focus:ring-emerald-500"
              >
                {REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes / Details (請假原因說明)</label>
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="e.g. Doctor's appointment / Fever"
                className="w-full border rounded-lg p-2 text-xs focus:ring-2 focus:ring-emerald-500"
                rows="3"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-xs font-bold transition shadow-sm disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Request (送出請假)'}
            </button>
          </form>
        </div>

        {/* History Table */}
        <div className="md:col-span-2 bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-800 dark:text-gray-200 text-base mb-4 flex items-center justify-between">
            <span>📋 My Leave History (請假與補課紀錄)</span>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{requests.length} records</span>
          </h3>

          {loading ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">Loading history...</p>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-xs">
              No leave requests yet. Fill out the form on the left if you need to request leave.
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => {
                const statusStyles = {
                  pending: 'bg-yellow-50 text-yellow-800 border-yellow-200',
                  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
                  rejected: 'bg-red-50 text-red-800 border-red-200',
                };
                const statusLabel = {
                  pending: '待審核 Pending',
                  approved: '已核准 Approved',
                  rejected: '已婉拒 Rejected',
                };

                return (
                  <div key={r.id} className="p-3.5 border rounded-lg hover:border-gray-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 dark:text-gray-100 text-sm">
                            {new Date(r.request_date).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit', weekday: 'short' })}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {r.class_name || 'Class Session'}
                          </span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${statusStyles[r.status] || 'bg-gray-100 dark:bg-gray-700'}`}>
                            {statusLabel[r.status] || r.status}
                          </span>
                        </div>

                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Reason: <span className="font-medium text-gray-800 dark:text-gray-200">{r.reason_category}</span>
                          {r.reason_text && ` — "${r.reason_text}"`}
                        </div>

                        {r.admin_notes && (
                          <div className="text-xs text-blue-600 bg-blue-50 p-1.5 rounded mt-2">
                            💬 Teacher note: {r.admin_notes}
                          </div>
                        )}

                        {/* Scheduled Make-Up Slot */}
                        {r.makeup_scheduled && (
                          <div className="mt-2.5 p-2 bg-emerald-50 rounded border border-emerald-200 text-xs">
                            <span className="font-bold text-emerald-900">
                              📅 Make-Up Arranged (補課時間):
                            </span>{' '}
                            <span className="font-semibold text-emerald-800">
                              {new Date(r.makeup_date).toLocaleDateString('zh-TW')} {r.makeup_time}
                            </span>
                            {r.makeup_notes && (
                              <div className="text-emerald-700 text-[11px] mt-0.5">{r.makeup_notes}</div>
                            )}
                          </div>
                        )}
                      </div>

                      {r.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleCancel(r.id)}
                          className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
